import React, { useMemo } from 'react'
import { ReasoningStep } from './ReasoningStep'
import { ToolCallCard } from './ToolCallCard'
import { useThemeManager } from '../../../../_hooks/useThemeManager'
import { formatMarkdown } from '../utils/TextUtils'

interface ProcessStep {
  id: string
  text: string
  status: 'completed' | 'active' | 'pending'
}

interface ToolCall {
  id: string
  type:
    | 'grep'
    | 'search'
    | 'code'
    | 'edit'
    | 'delete'
    | 'todo'
    | 'read'
    | 'navigate'
    | 'panel'
    | 'clipboard'
  action: string
  context: string
  details?: string | Record<string, unknown>
  result?: any
  seq?: number
  status: 'completed' | 'running' | 'pending'
  isFrontend?: boolean
}

interface Reasoning {
  id: string
  seq: number
  summary: string[]
}

interface ReasoningTraceProps {
  isThinking: boolean
  processSteps?: ProcessStep[]
  toolCalls?: ToolCall[]
  isExpanded?: boolean
  reasonings?: Reasoning[]
  showLatestRunningBadge?: boolean
}

export const ThinkingPanel: React.FC<ReasoningTraceProps> = ({
  isThinking,
  processSteps = [],
  toolCalls = [],
  isExpanded = false,
  showLatestRunningBadge = false,
  reasonings = []
}) => {
  const { isDarkTheme } = useThemeManager()
  const timelineGroups = useMemo(
    () =>
      [
        ...reasonings.map((reasoning) => ({
          seq: reasoning.seq,
          reasoning: reasoning.summary.filter(Boolean).join('\n\n'),
          toolCalls: [] as ToolCall[]
        })),
        ...toolCalls.map((toolCall) => ({
          seq: toolCall.seq ?? Number.MAX_SAFE_INTEGER,
          reasoning: '',
          toolCalls: [toolCall]
        }))
      ].sort((left, right) => left.seq - right.seq),
    [reasonings, toolCalls]
  )

  return (
    <div className="flex flex-col gap-2 ">
      {/* Thinking Process Panel */}
      {isExpanded && (toolCalls.length > 0 || processSteps.length > 0 || reasonings.length > 0) && (
        <div
          data-testid="thinking-process-panel"
          className={`flex flex-col mb-2 gap-2 p-2 rounded-lg w-full ${
            isDarkTheme ? 'bg-[color:var(--surface-muted)]' : 'bg-[color:var(--surface-muted)]'
          }`}>
          {/* Process Steps */}
          {processSteps.length > 0 && (
            <div className="flex flex-col gap-1">
              {processSteps.map((step, index) => (
                <ReasoningStep
                  key={step.id}
                  text={step.text}
                  status={step.status}
                  showLine={index < processSteps.length - 1}
                  isAnimating={step.status === 'active' && isThinking}
                />
              ))}
            </div>
          )}

          {/* Tool Calls */}
          {timelineGroups.length > 0 && (
            <div className="flex flex-col gap-2">
              {timelineGroups.map((group, groupIndex) => (
                <div
                  key={`${group.toolCalls[0]?.id || 'message'}-${groupIndex}`}
                  className="flex flex-col gap-1"
                  data-testid="reasoning-group">
                  {group.reasoning && (
                    <div
                      className="subtitle-text leading-[1.6] markdown-reasoning break-words max-w-full"
                      data-testid="reasoning-segment"
                      dangerouslySetInnerHTML={{ __html: formatMarkdown(group.reasoning) }}
                    />
                  )}
                  {group.toolCalls.map((toolCall) => {
                    const toolCallIndex = toolCalls.indexOf(toolCall)
                    return (
                      <ToolCallCard
                        key={toolCall.id}
                        type={toolCall.type}
                        action={toolCall.action}
                        context={toolCall.context}
                        details={toolCall.details}
                        result={toolCall.result}
                        status={toolCall.status}
                        isFrontend={toolCall.isFrontend}
                        showRunningBadge={
                          showLatestRunningBadge && toolCallIndex === toolCalls.length - 1
                        }
                      />
                    )
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
